import {
  Box,
  Button,
  Flex,
  Image,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  useMediaQuery,
} from '@chakra-ui/react';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toWei } from 'web3-utils';
import useWeb3 from '../../../hooks/useWeb3';
import { useTransactionAdder } from '../../../redux/transactions/hooks';
import { callEstGas } from '../../../utils/callHelpers';
import { formatBondBalance } from '../../../utils/common';
import { useResponseModal } from '../../../utils/confirmModal';
import { getBondContract } from '../../../utils/contractHelpers';

export const CantPurchaseModal = ({ isOpenModal, onDismiss = () => null }) => {
  const { t } = useTranslation('bonds');
  const [isLargerThan480] = useMediaQuery('(max-width: 480px ) and (min-width: 0px )');

  return (
    <Modal blockScrollOnMount={false} isOpen={isOpenModal} onClose={onDismiss} isCentered={!isLargerThan480}>
      <ModalOverlay width="100%" height="100%" />
      <ModalContent
        mt={['126px', '3.75rem']}
        mx={['16px', '0px']}
        px={['12px', '16px', '24px']}
        pt="20px"
        borderRadius="24px"
        w={['410px']}
        // height={['480px', '480px', '480px', '600px', '800px', '800px']}
        height={['auto']}
        maxH="320px"
        color="white"
        bg="gray.800"
        boxShadow={'0px 8px 32px rgba(0, 0, 0, 0.25)'}
      >
        <ModalCloseButton size="lg" color="white" />
        <ModalBody fontSize="14px" bg="none" textAlign="center" color="white" mb="24px" py="0px" px={'0px'}>
          <Flex
            fontSize={['24px', '32px']}
            fontWeight={600}
            color={'teal.150'}
            flexDirection={'column'}
            alignItems={'center'}
          >
            <Image w={['75px']} h={['75px']} src="/img/times-circle-red.svg" />
            <Text fontWeight={600} fontSize={['24px']} mt={['12px']} color={'gray.100'}>
              {t('purchase_failed')}
            </Text>
            <Text
              fontWeight={400}
              as="span"
              display={'inline-block'}
              fontSize={['14px']}
              mt={['24px']}
              color={'gray.400'}
            >
              {t('desc_cant_purchase')}&nbsp;{t('please_read')}&nbsp;"
              <Text fontWeight={700} as="span" color={'gray.400'}>
                {t('sale_rule')}
              </Text>
              "&nbsp;{t('for_more_info')}
            </Text>
            <Button
              _hover={{
                bg: 'green.500',
              }}
              _active={{
                bg: 'green.500',
              }}
              w={'200px'}
              mt={['24px']}
              color={'white'}
              bg={'teal.150'}
              borderRadius={'8px'}
              fontWeight={500}
              fontSize={['14px']}
              onClick={onDismiss}
            >
              {t('acknowledge')}
            </Button>
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default function ConfirmPurchase({ isOpen, onClose, amount, price, assetId, account, bondAddress }) {
  const { t } = useTranslation('bonds');
  const [isLargerThan480] = useMediaQuery('(max-width: 480px ) and (min-width: 0px )');
  const addTransaction = useTransactionAdder();
  const web3 = useWeb3();
  const onPurchase = useCallback(async () => {
    if (account) {
      try {
        const contract = getBondContract(bondAddress, web3);
        const total = Number(amount) * Number(price);
        const totalWei = toWei(total.toFixed(6), 'ether');
        const method = contract.methods.purchase(totalWei);
        let gas;
        try {
          gas = await callEstGas(method, account);
        } catch (e) {}
        method.send({ from: account, gas }, async function (err, hash) {
          if (hash) {
            addTransaction({ hash } as any);
            await useResponseModal({
              title: t('transaction_submitted'),
              txHash: hash,
            });
          }
          if (err) {
            if (err?.message) {
              if (err?.message) {
                await useResponseModal({
                  title: t('purchase_failed'),
                  errorMsg: err?.message,
                });
              }
            }
            throw err;
          }
        });
      } catch (e) {
        await useResponseModal({
          title: t('purchase_failed'),
          errorMsg: e?.message,
        });
      }
    }
  }, [account, amount, price, web3, bondAddress]);
  return (
    <Modal blockScrollOnMount={false} isOpen={isOpen} onClose={onClose} isCentered={!isLargerThan480}>
      <ModalOverlay width="100%" height="100%" />
      <ModalContent
        mt={['126px', '3.75rem']}
        mx={['16px', '0px']}
        px={['12px', '16px', '24px']}
        pt="20px"
        borderRadius="24px"
        w={['410px']}
        // height={['480px', '480px', '480px', '600px', '800px', '800px']}
        height={['auto']}
        maxH="320px"
        color="white"
        bg="gray.800"
        boxShadow={'0px 8px 32px rgba(0, 0, 0, 0.25)'}
      >
        <ModalHeader p={['0px']} textAlign={'center'}>
          <Text fontSize={['14px', '16px']} fontWeight={500} color={'gray.100'}>
            {t('confirm_purchase')}
          </Text>
        </ModalHeader>
        <ModalCloseButton size="lg" color="white" />
        <ModalBody fontSize="14px" bg="none" textAlign="center" color="white" mb="24px" py="0px" px={'0px'}>
          <Text fontSize={['24px', '32px']} fontWeight={600} color={'teal.150'}>
            {assetId}
          </Text>
          <Box
            px={['16px']}
            py={['12px']}
            fontWeight={500}
            color={'gray.500'}
            mt={['20px']}
            background={'gray.900'}
            borderRadius={'8px'}
          >
            <Flex justifyContent={'space-between'}>
              <Text fontSize={['14px']}>{t('amount')}:</Text>
              <Text fontSize={['14px', '16px']} color={'gray.100'}>
                {formatBondBalance(Number(amount))}
              </Text>
            </Flex>
            <Flex justifyContent={'space-between'}>
              <Text fontSize={['14px']}>{t('price')}:</Text>
              <Text fontSize={['14px', '16px']}>{formatBondBalance(Number(price))}</Text>
            </Flex>
            <Box bg={'gray.700'} width={'100%'} height={'1px'} mt={['8px']} mb={['12px']} />
            <Flex fontWeight={600} fontSize={'20px'} color={'gray.200'} justifyContent={'space-between'}>
              <Text>{t('total')}:</Text>
              <Text>{formatBondBalance(Number(amount) * Number(price))}</Text>
            </Flex>
          </Box>
          <Button
            _hover={{
              bg: 'green.500',
            }}
            _active={{
              bg: 'green.500',
            }}
            width={'100%'}
            mt={['24px']}
            color={'white'}
            bg={'teal.150'}
            borderRadius={'8px'}
            fontWeight={500}
            fontSize={['14px']}
            onClick={onPurchase}
          >
            {t('confirm_purchase')}
          </Button>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
